import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import * as React from "react";
import { ReactNode, useEffect, useState } from "react";
import { connect } from "react-redux";
import Container from "reactstrap/lib/Container";
import { compose } from "recompose";
import { AppState } from "../../../common/appState";
import { sleepAsync } from "../../../common/functions";
import * as baseStore from "../../../store/BaseStore";
import { ApplicationState } from "../../../store/configureStore";
import Footer from "./Footer/Footer";
import "./Layout.css";
import NavMenu from "./NavMenu";

interface OuterProps {
    children: React.ReactNode;
}

type InnerProps = OuterProps & baseStore.BaseState;

function Layout({ children, isHeaderShown, isFooterShown }: InnerProps) {
    const [panels, setPanels] = useState<ReactNode[]>([]);

    useEffect(() => {
        (async () => {
            await sleepAsync(500);
            const earlyPanels = await dynamicImportPanelsAndDialogs(true);
            setPanels(earlyPanels);

            await sleepAsync(3500);
            const latePanels = await dynamicImportPanelsAndDialogs(false);
            setPanels([...earlyPanels, ...latePanels]);
        })();
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <div>
                {isHeaderShown && <NavMenu />}

                <Container className="contents-container">{children}</Container>

                <Footer isFooterShown={isFooterShown} />

                {panels}
            </div>
        </ThemeProvider>
    );
}

export const panelsToImport: {
    importComponent: () => Promise<{ default: () => JSX.Element }>;
    getOpenStatus: (state: AppState) => boolean | number;
    early: boolean;
}[] = [
    {
        importComponent: () => import("./Login/Panel"),
        getOpenStatus: state => state.signInPanelState.type !== "close",
        early: true,
    },
    {
        importComponent: () =>
            import("./Login/SignUp/TermOfUse/TermOfUsePanel"),
        getOpenStatus: state => state.termOfUsePanelState.open,
        early: false,
    },
    {
        importComponent: () =>
            import("../../shared/User/OtherUserPanel/OtherUserPanel"),
        getOpenStatus: state =>
            state.otherUserPanelsState.filter(panelState => panelState.open)
                .length,
        early: true,
    },
    {
        importComponent: () =>
            import("../../shared/User/PublicUserProfilePanel/Panel"),
        getOpenStatus: state =>
            state.publicUserProfilePanelsState.filter(
                panelState => panelState.open
            ).length,
        early: true,
    },
    {
        importComponent: () => import("../../shared/Panel/ImageDetail/Panel"),
        getOpenStatus: state => state.imageDetailPanelState.open,
        early: false,
    },
    {
        importComponent: () =>
            import("../../shared/User/UnapprovedUserListPanel/Panel"),
        getOpenStatus: state => state.unapprovedUserListPanelState.open,
        early: true,
    },
    {
        importComponent: () =>
            import("../../shared/User/StudentSearchPanel/Panel"),
        getOpenStatus: state => state.studentSearchPanelState.open,
        early: true,
    },
    {
        importComponent: () =>
            import("../../shared/Batch/BatchCreationPanel/Panel"),
        getOpenStatus: state => state.batchCreationPanelState.open,
        early: true,
    },
    {
        importComponent: () =>
            import(
                "../../shared/User/OtherUserPanel/components/LessonRecordArea/TestLessonCard/OriginalTestResultPanel/Panel"
            ),
        getOpenStatus: state => state.testResultPanelState.open,
        early: false,
    },
    {
        importComponent: () =>
            import("../../shared/Markdown/SamplePanel/Panel"),
        getOpenStatus: state => state.markdownSamplePanelState.open,
        early: false,
    },
    {
        importComponent: () => import("../zApps/parts/Help/HelpPanel/Panel"),
        getOpenStatus: state => state.helpPanelState.open,
        early: false,
    },
    {
        importComponent: () =>
            import("../zApps/Teacher/BatchStudents/ProgressTables/Panel"),
        getOpenStatus: state => state.studentsProgressPanelState.open,
        early: false,
    },
    {
        importComponent: () =>
            import(
                "../zApps/Teacher/BatchStudents/CourseBulkActionPanel/Panel"
            ),
        getOpenStatus: state => state.courseBulkActionPanelState.open,
        early: false,
    },
    {
        importComponent: () =>
            import(
                "../zApps/Admin/LessonEdit/EditArea/TestEditArea/PreviewPanel/Panel"
            ),
        getOpenStatus: state => state.testPreviewPanelState.open,
        early: false,
    },
    {
        importComponent: () =>
            import("../zApps/Student/Lesson/VideoLesson/PreviewPanel/Panel"),
        getOpenStatus: state => state.videoPreviewPanelState.open,
        early: false,
    },
];

export const dialogsToImport: {
    importComponent: () => Promise<{ default: () => JSX.Element }>;
    early: boolean;
}[] = [
    {
        importComponent: () =>
            import("../../shared/Dialog/SimpleDialog/SimpleDialog"),
        early: true,
    },
];

async function dynamicImportPanelsAndDialogs(early: boolean) {
    const PanelComponents = [...panelsToImport, ...dialogsToImport]
        .filter(c => c.early === early)
        .map(async (p, i) => {
            const { default: Component } = await p.importComponent();
            return (
                <Component key={`${p.early ? "early" : "late"}-panel${i}`} />
            );
        });
    return await Promise.all(PanelComponents);
}

export const theme = createTheme({
    palette: {
        primary: { main: "#007BFF" },
        secondary: { main: "#dc3545" },
        success: { main: "#28a745" },
    },
    typography: {
        button: {
            textTransform: "none",
        },
    },
});

export default compose<InnerProps, OuterProps>(
    connect((state: ApplicationState) => state.base)
)(Layout);
